import {
  createSlice,
  createAction,
  ActionReducerMapBuilder,
  PayloadAction,
} from '@reduxjs/toolkit'
import {
  AccountList,
  CurrentUser,
  GetTokenReqType,
  SetSyncErrorType,
  SliceStatus,
  UserRoles,
  SetTokenResponse,
  InstallerType,
} from 'interfaces'
import {loginFailure, loginSuccess, login} from './Login/loginActions'

import {
  storageLocation,
  storageLocationSuccess,
  storageLocationFailure,
  updateCurrentStep,
} from './Onboarding/onboardingActions'

const checkSession = createAction<UserRoles>('CheckSession')
const logData = createAction<string>('logData')

const logout = createAction('Logout')
const logoutFailure = createAction<string>('logoutFailure')

const setUser = createAction<CurrentUser>('SetUser')
const resetUser = createAction<null>('ResetUser')

const setTokens = createAction<GetTokenReqType>('SetTokens')
const setTokensFailure = createAction('SetTokensFailure')
const setTokensSuccess = createAction<SetTokenResponse>('SetTokensSuccess')
const getAccountsList = createAction('getAccountsList')
const getAccountsListFailure = createAction<string>('getAccountsListFailure')
const getAccountsListSuccess = createAction<AccountList>(
  'getAccountsListSuccess',
)
const refreshTokens = createAction<string>('refreshTokens')
const setAsyncError = createAction<SetSyncErrorType>('SetAsyncError')
const resetAsyncError = createAction<string>('ResetAsyncError')

const setNotification = createAction<{
  messageType: 'success' | 'warning' | 'error'
  message: string
  navigateTo?: string
}>('SetNotification')
const resetNotification = createAction<{
  messageType: 'none'
  message: ''
  navigateTo?: string
}>('ResetNotification')

const downloadInstaller = createAction<{
  bundle: InstallerType
}>('downloadInstaller')
const downloadInstallerFailure = createAction('downloadInstallerFailure')
const downloadInstallerSuccess = createAction<{
  fileUrl: string
}>('downloadInstallerSuccess')

const setUpdatedAt = createAction<string>('setUpdatedAt')
export const setUsersImported = createAction<boolean>('setUsersImported')
export const setImportStatusTimerRunning = createAction<boolean>(
  'setImportStatusTimerRunning',
)

export type UserSliceState = {
  current: CurrentUser
  name: string
  tokens: {
    accessToken: string
    refreshToken: string
  }
  emailId: string
  role: string
  authProvider: string
  updated_at: string
  usersImported: boolean
  primaryDomain: string
  onboardingCompleted: boolean
  status: SliceStatus
  error: string
  notification: {
    messageType: 'success' | 'warning' | 'error' | 'none'
    message: string
    navigateTo?: string
  }
  downloadInstallerStatus: SliceStatus
  multiAccount: {
    details: AccountList
    status: SliceStatus
  }
  tokenStatus: SliceStatus
  locationStatus: SliceStatus
  currentStep: number
  isImportStatusTimerRunning: boolean
}
const userSlice = createSlice({
  name: 'user',
  initialState: {
    current: null as CurrentUser,
    name: '',
    tokens: {
      accessToken: '',
      refreshToken: '',
    },
    emailId: '',
    role: '',
    authProvider: '',
    updated_at: '',
    usersImported: false,
    primaryDomain: '',
    onboardingCompleted: false,
    status: SliceStatus.idle,
    downloadInstallerStatus: SliceStatus.idle,
    locationStatus: SliceStatus.idle,
    error: '',
    notification: {
      messageType: 'none',
      message: '',
    } as {
      messageType: 'success' | 'warning' | 'error' | 'none'
      message: string
    },
    multiAccount: {
      details: {} as AccountList,
      status: SliceStatus.idle,
    },
    tokenStatus: SliceStatus.idle,
    currentStep: parseInt(localStorage.getItem('currentStep') || '1', 10),
    isImportStatusTimerRunning: false,
  },
  reducers: {
    setOnboarding(state, action: PayloadAction<boolean>) {
      state.onboardingCompleted = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<UserSliceState>) =>
    builder
      .addCase(login, state => ({
        ...state,
        status: SliceStatus.pending,
      }))
      .addCase(loginFailure, state => ({
        ...state,
        status: SliceStatus.rejected,
      }))
      .addCase(loginSuccess, state => ({
        ...state,
        status: SliceStatus.resolved,
      }))
      .addCase(setUser, (state, action) => ({
        ...state,
        current: action.payload,
      }))
      .addCase(resetUser, (state, action) => ({
        ...state,
        current: action.payload,
      }))
      .addCase(storageLocation, state => ({
        ...state,
        locationStatus: SliceStatus.pending,
      }))
      .addCase(storageLocationSuccess, state => ({
        ...state,
        locationStatus: SliceStatus.resolved,
      }))
      .addCase(storageLocationFailure, state => ({
        ...state,
        locationStatus: SliceStatus.rejected,
      }))
      .addCase(setTokens, state => ({
        ...state,
        tokenStatus: SliceStatus.pending,
      }))
      .addCase(setTokensSuccess, (state, {payload: {respData}}) => ({
        ...state,
        tokens: {
          accessToken: respData.accessToken,
          refreshToken: respData.refreshToken,
        },
        name: respData.name,
        emailId: respData.emailId,
        authProvider: respData.authProvider,
        primaryDomain: respData.primaryDomain,
        tokenStatus: SliceStatus.resolved,
        usersImported: respData.usersImported,
        role: respData.role,
      }))
      .addCase(setTokensFailure, state => ({
        ...state,
        tokenStatus: SliceStatus.rejected,
      }))
      .addCase(setUpdatedAt, (state, action) => ({
        ...state,
        updated_at: action.payload,
      }))
      .addCase(setUsersImported, (state, action) => ({
        ...state,
        usersImported: action.payload,
      }))
      .addCase(setImportStatusTimerRunning, (state, action) => ({
        ...state,
        isImportStatusTimerRunning: action.payload,
      }))
      .addCase(refreshTokens, (state, action) => ({
        ...state,
        tokens: {...state.tokens, accessToken: action.payload},
      }))
      .addCase(setNotification, (state, action) => ({
        ...state,
        notification: action.payload,
      }))
      .addCase(resetNotification, (state, action) => ({
        ...state,
        notification: action.payload,
      }))
      .addCase(setAsyncError, (state, action) => ({
        ...state,
        error:
          action.payload.filter === 'user'
            ? state.error
            : action.payload?.message,
      }))
      .addCase(resetAsyncError, (state, action) => ({
        ...state,
        error: action.payload === 'user' ? state.error : '',
      }))
      .addCase(downloadInstaller, state => ({
        ...state,
        downloadInstallerStatus: SliceStatus.pending,
      }))
      .addCase(downloadInstallerFailure, state => ({
        ...state,
        downloadInstallerStatus: SliceStatus.rejected,
      }))
      .addCase(downloadInstallerSuccess, state => ({
        ...state,
        downloadInstallerStatus: SliceStatus.resolved,
      }))
      .addCase(updateCurrentStep, (state, action) => ({
        ...state,
        currentStep: action.payload,
      }))
      .addCase(getAccountsList, state => ({
        ...state,
        multiAccount: {
          details: {} as AccountList,
          status: SliceStatus.pending,
        },
      }))
      .addCase(getAccountsListFailure, state => ({
        ...state,
        multiAccount: {
          details: {} as AccountList,
          status: SliceStatus.rejected,
        },
      }))
      .addCase(getAccountsListSuccess, (state, action) => ({
        ...state,
        multiAccount: {
          details: action.payload,
          status: SliceStatus.resolved,
        },
      }))
      .addDefaultCase(state => state),
})

const {setOnboarding} = userSlice.actions

export const {reducer: userReducer, name: userReducerName} = userSlice

export type TUserActions =
  | ReturnType<typeof setUser>
  | ReturnType<typeof setTokens>
  | ReturnType<typeof setTokensFailure>
  | ReturnType<typeof setTokensSuccess>
  | ReturnType<typeof refreshTokens>
  | ReturnType<typeof setOnboarding>
  | ReturnType<typeof resetUser>
  | ReturnType<typeof setAsyncError>
  | ReturnType<typeof resetAsyncError>
  | ReturnType<typeof setNotification>
  | ReturnType<typeof resetNotification>
  | ReturnType<typeof login>
  | ReturnType<typeof loginSuccess>
  | ReturnType<typeof loginFailure>
  | ReturnType<typeof logout>
  | ReturnType<typeof logoutFailure>
  | ReturnType<typeof checkSession>
  | ReturnType<typeof logData>
  | ReturnType<typeof setUpdatedAt>
  | ReturnType<typeof setUsersImported>
  | ReturnType<typeof downloadInstaller>
  | ReturnType<typeof downloadInstallerFailure>
  | ReturnType<typeof downloadInstallerSuccess>
  | ReturnType<typeof getAccountsList>
  | ReturnType<typeof getAccountsListFailure>
  | ReturnType<typeof getAccountsListSuccess>
  | ReturnType<typeof storageLocation>
  | ReturnType<typeof storageLocationSuccess>
  | ReturnType<typeof storageLocationFailure>
  | ReturnType<typeof updateCurrentStep>
  | ReturnType<typeof setImportStatusTimerRunning>

export const userActions = {
  setUser,
  setTokens,
  setTokensFailure,
  setTokensSuccess,
  refreshTokens,
  setOnboarding,
  resetUser,
  setAsyncError,
  resetAsyncError,
  setNotification,
  resetNotification,
  login,
  loginSuccess,
  loginFailure,
  logout,
  logoutFailure,
  checkSession,
  logData,
  setUpdatedAt,
  setUsersImported,
  downloadInstaller,
  downloadInstallerFailure,
  downloadInstallerSuccess,
  getAccountsList,
  getAccountsListFailure,
  getAccountsListSuccess,
  storageLocation,
  storageLocationSuccess,
  storageLocationFailure,
  updateCurrentStep,
  setImportStatusTimerRunning,
}

export type UserState = ReturnType<typeof userReducer>
